<template>
  <div class="package-page">
    <header class="page-heading">
      <div>
        <p>Acquisitions · Colis</p>
        <h1>{{ isNew ? "Nouveau colis" : `Colis ${form.reference}` }}</h1>
        <span
          >Saisis les frais de l’envoi une fois : ils sont répartis sur les lots
          et reportés dans leurs coûts.</span
        >
      </div>
      <a href="/packages" class="back" @click.prevent="leave">{{
        cameFromApp ? "← Retour" : "← Colis"
      }}</a>
    </header>

    <div v-if="loading" class="panel empty">Chargement…</div>
    <template v-else>
      <!-- 01 Le colis -->
      <section class="panel">
        <div class="section-heading">
          <b>01</b>
          <div>
            <h2>L’envoi</h2>
            <p>Montants en yens tels qu’affichés par l’intermédiaire.</p>
          </div>
        </div>
        <div class="grid grid--package">
          <label class="field"
            >Référence du colis
            <input
              v-model.trim="form.reference"
              class="input"
              placeholder="#79"
          /></label>
          <label class="field"
            >Expéditeur
            <select v-model="form.sender" class="input">
              <option
                v-for="intermediary in setup.intermediaries"
                :key="intermediary.id"
                :value="intermediary.id"
              >
                {{ intermediary.name }}
              </option>
              <option
                v-for="(label, id) in PACKAGE_SENDERS"
                :key="id"
                :value="id"
              >
                {{ label }}
              </option>
            </select></label
          >
          <label class="field"
            >Frais de colis
            <span class="affix"
              ><input
                v-model.number="form.package_fee_jpy"
                class="input"
                type="number"
                min="0"
              /><span>¥</span></span
            ><small>{{ yenToEur(form.package_fee_jpy) }}</small></label
          >
          <label class="field"
            >Frais de port
            <span class="affix"
              ><input
                v-model.number="form.shipping_fee_jpy"
                class="input"
                type="number"
                min="0"
              /><span>¥</span></span
            ><small>{{ yenToEur(form.shipping_fee_jpy) }}</small></label
          >
        </div>
        <!-- Who sent it, when, and where to follow it -->
        <div class="grid grid--package grid--shipment">
          <label v-if="!isIntermediary" class="field"
            >Nom de l’expéditeur
            <input
              v-model.trim="form.sender_name"
              class="input"
              placeholder="Amazon, Jean (Leboncoin)…"
          /></label>
          <label class="field"
            >Date d’envoi
            <input v-model="form.shipped_on" class="input" type="date"
          /></label>
          <label class="field"
            >Reçu le
            <input
              v-model="form.received_on"
              class="input"
              type="date"
            /><small>{{
              form.received_on ? "Colis reçu" : "Vide : en route"
            }}</small></label
          >
          <label class="field field--tracking"
            >Lien de suivi
            <span class="tracking"
              ><input
                v-model.trim="form.tracking_url"
                class="input"
                type="url"
                inputmode="url"
                placeholder="https://…"
              /><a
                v-if="trackingIsLink"
                :href="form.tracking_url"
                target="_blank"
                rel="noopener noreferrer"
                class="tracking__open"
                >Suivre ↗</a
              ></span
            ></label
          >
        </div>
        <div class="options">
          <p v-if="!rateOpen" class="options__line">
            Taux de change : 1 € = {{ rateText }} ¥ · {{ rateOrigin }} ·
            <button type="button" class="link" @click="rateOpen = true">
              Modifier
            </button>
          </p>
          <label v-else class="field field--rate"
            >Taux de change
            <span class="affix"
              ><span>1 € =</span
              ><input
                v-model.number="form.jpy_per_eur"
                class="input"
                type="number"
                min="1"
                step="0.01"
              /><span>¥</span></span
            ><small
              >{{ rateOrigin }} (Xe.com…).
              <button
                v-if="setup.jpy_per_eur && !rateIsToday"
                type="button"
                class="link"
                @click="form.jpy_per_eur = setup.jpy_per_eur"
              >
                Reprendre le taux du jour ({{ setup.jpy_per_eur }})
              </button></small
            ></label
          >
        </div>
      </section>

      <!-- 02 Les lots -->
      <section class="panel">
        <div class="section-heading">
          <b>02</b>
          <div>
            <h2>Les lots du colis</h2>
            <p>
              Frais propres à chaque lot. Le port et le colis sont répartis à
              parts égales<template v-if="sharesVisible"
                >, sauf « part » différente : 2 pour un lot deux fois plus
                encombrant…</template
              ><template v-else
                >.
                <button type="button" class="link" @click="showShares = true">
                  Plus d’options
                </button></template
              >
            </p>
          </div>
        </div>

        <div class="add-lot">
          <input
            v-model="search"
            class="input"
            type="search"
            placeholder="Ajouter un achat : titre, identifiant ou référence…"
            @focus="pickerOpen = true"
          />
          <ul v-if="pickerOpen && candidates.length" class="picker">
            <li v-for="purchase in candidates" :key="purchase.offer_id">
              <button type="button" @click="addLot(purchase)">
                <strong>{{ purchase.title || purchase.offer_id }}</strong>
                <small
                  >#{{ purchase.offer_id
                  }}{{ purchase.reference ? ` · ${purchase.reference}` : ""
                  }}{{
                    purchase.packageRef
                      ? ` · déjà dans le colis ${purchase.packageRef}`
                      : ""
                  }}</small
                >
              </button>
            </li>
          </ul>
          <p v-else-if="pickerOpen && search" class="hint">
            Aucun achat sans colis ne correspond.
          </p>
        </div>

        <p v-if="sameRefPurchases.length" class="hint">
          {{ sameRefPurchases.length }} achat{{
            sameRefPurchases.length > 1 ? "s" : ""
          }}
          déjà noté{{ sameRefPurchases.length > 1 ? "s" : "" }} dans le colis
          {{ form.reference }}.
          <button type="button" class="link" @click="addSameRef">
            Les ajouter
          </button>
        </p>
        <p v-else-if="!form.lots.length" class="hint">
          Ajoute les achats qui partent dans ce colis.
        </p>
        <div v-else class="lots" :class="{ 'lots--shares': sharesVisible }">
          <div class="lots__head">
            <span>Lot</span><span>Prix</span><span>TVA locale</span
            ><span>Frais</span><span>Port local</span
            ><span v-if="sharesVisible">Part</span
            ><span class="num">Colis + port</span
            ><span class="num">TVA / douane</span><span class="num">Total</span
            ><span></span>
          </div>
          <article v-for="lot in form.lots" :key="lot.offer_id" class="lot">
            <div class="lot__title">
              <EntityLink type="purchase" :id="lot.offer_id" class="lot__link"
                ><strong>{{
                  purchaseOf(lot).title || lot.offer_id
                }}</strong></EntityLink
              >
              <small
                >#{{ lot.offer_id }} · {{ sourceName(purchaseOf(lot).source)
                }}{{ listingPrice(purchaseOf(lot)) }}</small
              >
            </div>
            <label
              ><span class="lot__label">Prix</span
              ><span class="affix"
                ><input
                  v-model.number="lot.price_eur"
                  class="input"
                  type="number"
                  min="0"
                  step="0.01"
                /><span>€</span></span
              ></label
            >
            <label
              ><span class="lot__label">TVA locale</span
              ><select v-model.number="lot.local_tax_rate" class="input">
                <option
                  v-for="rate in setup.local_tax_rates"
                  :key="rate"
                  :value="rate"
                >
                  {{ rate === 0 ? "Pas de TVA" : `+ ${rate} %` }}
                </option>
              </select></label
            >
            <label
              ><span class="lot__label">Frais</span
              ><span class="affix"
                ><input
                  v-model.number="lot.fee_jpy"
                  class="input"
                  type="number"
                  min="0"
                /><span>¥</span></span
              ></label
            >
            <label
              ><span class="lot__label">Port local</span
              ><span class="affix"
                ><input
                  v-model.number="lot.local_shipping_jpy"
                  class="input"
                  type="number"
                  min="0"
                /><span>¥</span></span
              ></label
            >
            <label v-if="sharesVisible"
              ><span class="lot__label">Part</span
              ><input
                v-model.number="lot.share"
                class="input input--share"
                type="number"
                min="0"
                step="0.5"
            /></label>
            <span class="computed"
              ><span class="lot__label">Colis + port</span
              >{{ computedOf(lot, (c) => c.package + c.shipping) }}</span
            >
            <span class="computed"
              ><span class="lot__label">TVA / douane</span
              >{{ computedOf(lot, (c) => c.taxes) }}</span
            >
            <strong class="computed computed--total"
              ><span class="lot__label">Total</span
              >{{ computedOf(lot, (c) => c.totalCost) }}</strong
            >
            <button
              type="button"
              class="remove"
              :aria-label="`Retirer ${purchaseOf(lot).title || lot.offer_id}`"
              @click="removeLot(lot)"
            >
              ×
            </button>
          </article>
        </div>
      </section>

      <!-- 03 TVA et douane -->
      <section class="panel">
        <div class="section-heading">
          <b>03</b>
          <div>
            <h2>TVA et douane</h2>
            <p>
              Selon le montant et le transporteur, la TVA est payée à l’envoi ou
              à l’arrivée, parfois avec des frais de dossier.
            </p>
          </div>
        </div>
        <div class="modes">
          <label
            class="mode"
            :class="{ active: form.import_mode === 'computed' }"
            ><input v-model="form.import_mode" type="radio" value="computed" />
            <span
              ><strong>Calculée</strong
              ><small
                >20 % de (prix + TVA locale + part du port), lot par lot</small
              ></span
            ></label
          >
          <label class="mode" :class="{ active: form.import_mode === 'actual' }"
            ><input v-model="form.import_mode" type="radio" value="actual" />
            <span
              ><strong>Montant payé</strong
              ><small
                >Débité sur la carte, frais de dossier compris : réparti au
                prorata de la valeur d’achat</small
              ></span
            ></label
          >
          <label v-if="form.import_mode === 'actual'" class="field paid"
            >Montant payé
            <span class="affix"
              ><input
                v-model.number="form.import_paid_eur"
                class="input"
                type="number"
                min="0"
                step="0.01"
              /><span>€</span></span
            ></label
          >
        </div>
      </section>

      <footer class="panel summary">
        <div class="summary__totals">
          <span
            >Port et colis
            <strong>{{
              money(
                (preview?.totals?.shipping || 0) +
                  (preview?.totals?.package || 0),
              )
            }}</strong></span
          >
          <span
            >TVA et douane
            <strong>{{ money(preview?.totals?.taxes) }}</strong></span
          >
          <span class="summary__total"
            >Total du colis
            <strong>{{ money(preview?.totals?.totalCost) }}</strong></span
          >
        </div>
        <p v-if="message" class="message" :class="{ error: messageIsError }">
          {{ message }}
        </p>
        <div class="summary__actions">
          <button
            v-if="!isNew"
            type="button"
            class="button button--danger"
            :disabled="saving"
            @click="remove"
          >
            Supprimer le colis
          </button>
          <button type="button" class="button button--secondary" @click="leave">
            Annuler
          </button>
          <button
            type="button"
            class="button button--primary"
            :disabled="saving || !form.lots.length"
            @click="save"
          >
            {{
              saving ? "Enregistrement…" : "Enregistrer et reporter les coûts"
            }}
          </button>
        </div>
      </footer>
    </template>
  </div>
</template>

<script>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import axios from "axios";
import config from "@/config/config";
import EntityLink from "../common/EntityLink.vue";
import {
  DEFAULT_SOURCE,
  PACKAGE_SENDERS,
  sourceLongLabel,
} from "../../sources";

export default {
  name: "PackageEditor",
  components: { EntityLink },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const packageId = computed(() => route.params.packageId);
    const isNew = computed(() => !packageId.value || packageId.value === "new");

    // Back where the editor was opened from (a sheet, the list…), else the list
    const cameFromApp = Boolean(window.history.state?.back);
    const leave = () => {
      if (cameFromApp) router.back();
      else router.push("/packages");
    };
    const loading = ref(true);
    const saving = ref(false);
    const message = ref("");
    const messageIsError = ref(false);
    const setup = ref({
      intermediaries: [],
      purchases: [],
      fee_defaults: {},
      local_tax_rates: [0, 8, 10],
    });
    // Today in the user's time ("2026-10-26"): shipping date of a new package
    const today = () => {
      const now = new Date();
      return [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
      ].join("-");
    };
    const form = reactive({
      reference: "",
      sender: "zenmarket",
      sender_name: "",
      shipped_on: today(),
      received_on: "",
      tracking_url: "",
      intermediary: "zenmarket",
      jpy_per_eur: null,
      package_fee_jpy: 0,
      shipping_fee_jpy: 0,
      import_mode: "computed",
      import_paid_eur: 0,
      lots: [],
    });
    // The intermediary (fees) follows the sender
    const isIntermediary = computed(() =>
      setup.value.intermediaries.some((entry) => entry.id === form.sender),
    );
    watch(
      () => form.sender,
      (sender) => {
        form.intermediary = setup.value.intermediaries.some(
          (entry) => entry.id === sender,
        )
          ? sender
          : null;
      },
    );
    const trackingIsLink = computed(() =>
      /^https?:\/\/\S+$/.test(form.tracking_url || ""),
    );
    const preview = ref(null);
    const search = ref("");
    const pickerOpen = ref(false);

    // Options hidden by default: exchange rate (kept with the package) and shares
    const rateOpen = ref(false);
    const rateIsToday = computed(
      () => Number(form.jpy_per_eur) === Number(setup.value.jpy_per_eur),
    );
    const rateText = computed(() =>
      new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(
        Number(form.jpy_per_eur) || 0,
      ),
    );
    const rateOrigin = computed(() =>
      rateIsToday.value
        ? "taux du jour"
        : isNew.value
          ? "taux modifié"
          : "taux enregistré avec le colis",
    );
    const showShares = ref(false);
    const sharesVisible = computed(
      () =>
        showShares.value ||
        form.lots.some((lot) => lot.share !== "" && Number(lot.share) !== 1),
    );

    const money = (value) =>
      new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(Number(value) || 0);
    const yenToEur = (value) =>
      form.jpy_per_eur > 0 && value
        ? `≈ ${money(Number(value) / form.jpy_per_eur)}`
        : "";
    // Last price seen on the listing, to check the price in euros
    const listingPrice = (purchase) =>
      purchase.currency === "JPY" && Number(purchase.price) > 0
        ? ` · ${purchase.final_price != null ? "prix final" : "annonce"} ${new Intl.NumberFormat("fr-FR").format(purchase.price)} ¥`
        : "";
    const sourceName = sourceLongLabel;
    const purchaseOf = (lot) =>
      setup.value.purchases.find(
        (purchase) => purchase.offer_id === lot.offer_id,
      ) || {};
    const computedOf = (lot, pick) => {
      const entry = preview.value?.lots?.find(
        (item) => item.offer_id === lot.offer_id,
      );
      return entry ? money(pick(entry.costs)) : "—";
    };

    // Purchases that can be added: not in another package, not already listed
    const candidates = computed(() => {
      const listed = new Set(form.lots.map((lot) => lot.offer_id));
      const text = search.value.trim().toLowerCase().replace(/^#/, "");
      return setup.value.purchases
        .filter((purchase) => !listed.has(purchase.offer_id))
        .filter(
          (purchase) =>
            !purchase.package_id || purchase.package_id === packageId.value,
        )
        .filter(
          (purchase) =>
            !text ||
            [
              purchase.title,
              purchase.offer_id,
              purchase.reference,
              purchase.packageRef,
            ]
              .filter(Boolean)
              .some((value) => String(value).toLowerCase().includes(text)),
        )
        .slice(0, 12);
    });

    const addLot = (purchase) => {
      const costs = purchase.costs || {};
      const rate = form.jpy_per_eur || setup.value.jpy_per_eur || 0;
      const fromYen = (amount) =>
        rate ? Math.round((Number(amount) / rate) * 100) / 100 : 0;
      // A saved price equal to the listing price in yen is yen entered as euros
      const inYen = purchase.currency === "JPY";
      const savedPrice = Number(costs.price) || 0;
      const price =
        savedPrice > 0 && !(inYen && savedPrice === Number(purchase.price))
          ? savedPrice
          : inYen && purchase.price
            ? fromYen(purchase.price)
            : Number(purchase.price) || 0;
      form.lots.push({
        offer_id: purchase.offer_id,
        price_eur: price,
        local_tax_rate: 0,
        fee_jpy:
          setup.value.fee_defaults?.[form.intermediary]?.[
            purchase.source || DEFAULT_SOURCE
          ] || 0,
        local_shipping_jpy:
          Number(costs.localShipping) > 0 && rate
            ? Math.round(Number(costs.localShipping) * rate)
            : 0,
        share: 1,
      });
      search.value = "";
      pickerOpen.value = false;
    };
    // Purchases whose package number was entered by hand before (packageRef)
    const sameRefPurchases = computed(() => {
      const reference = form.reference.replace(/^#/, "").toLowerCase();
      if (!reference) return [];
      const listed = new Set(form.lots.map((lot) => lot.offer_id));
      return setup.value.purchases.filter(
        (purchase) =>
          !listed.has(purchase.offer_id) &&
          (!purchase.package_id || purchase.package_id === packageId.value) &&
          String(purchase.packageRef || "")
            .replace(/^#/, "")
            .toLowerCase() === reference,
      );
    });
    const addSameRef = () => sameRefPurchases.value.forEach(addLot);
    const removeLot = (lot) => {
      form.lots = form.lots.filter((entry) => entry !== lot);
    };

    // Spread computed by the API as the form changes
    let timer = null;
    let previewRequest = 0;
    const refreshPreview = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        // Only the answer to the latest form counts (answers may arrive
        // out of order)
        const request = ++previewRequest;
        if (!form.lots.length || !(form.jpy_per_eur > 0)) {
          preview.value = null;
          return;
        }
        try {
          const { data } = await axios.post(
            `${config.apiUrl}/packages/preview`,
            form,
          );
          if (request !== previewRequest) return;
          preview.value = data;
          if (messageIsError.value) message.value = "";
        } catch (failure) {
          if (request !== previewRequest) return;
          message.value = failure.response?.data?.error || "Calcul impossible";
          messageIsError.value = true;
        }
      }, 300);
    };
    watch(form, refreshPreview, { deep: true });

    const load = async () => {
      loading.value = true;
      try {
        const { data } = await axios.get(`${config.apiUrl}/packages/setup`);
        setup.value = data;
        form.jpy_per_eur = data.jpy_per_eur;
        if (!isNew.value) {
          const { data: existing } = await axios.get(
            `${config.apiUrl}/packages/${packageId.value}`,
          );
          Object.assign(form, {
            reference: existing.reference,
            // Packages saved before the sender existed: their intermediary
            sender: existing.sender || existing.intermediary || "other",
            sender_name: existing.sender_name || "",
            shipped_on: existing.shipped_on || "",
            received_on: existing.received_on || "",
            tracking_url: existing.tracking_url || "",
            intermediary: existing.intermediary,
            jpy_per_eur: existing.jpy_per_eur,
            package_fee_jpy: existing.package_fee_jpy,
            shipping_fee_jpy: existing.shipping_fee_jpy,
            import_mode: existing.import_mode,
            import_paid_eur: existing.import_paid_eur,
            lots: existing.lots.map((lot) => ({ ...lot })),
          });
        }
      } catch (failure) {
        message.value = "Impossible de charger le colis.";
        messageIsError.value = true;
      } finally {
        loading.value = false;
        opened = JSON.stringify(form);
      }
    };

    // Leaving the form after a change (Retour, Annuler, back of the browser,
    // menu) asks first; not after saving or deleting
    let opened = "";
    let done = false;
    onBeforeRouteLeave(
      () =>
        done ||
        loading.value ||
        JSON.stringify(form) === opened ||
        window.confirm("Quitter sans enregistrer le colis ?"),
    );

    const save = async () => {
      saving.value = true;
      message.value = "";
      try {
        if (isNew.value) await axios.post(`${config.apiUrl}/packages`, form);
        else
          await axios.put(`${config.apiUrl}/packages/${packageId.value}`, form);
        done = true;
        leave();
      } catch (failure) {
        message.value =
          failure.response?.data?.error || "Enregistrement impossible";
        messageIsError.value = true;
      } finally {
        saving.value = false;
      }
    };

    const remove = async () => {
      if (
        !window.confirm(
          `Supprimer le colis ${form.reference} ? Ses lots gardent leurs frais propres, sans la part du colis, du port ni de la TVA.`,
        )
      )
        return;
      saving.value = true;
      try {
        await axios.delete(`${config.apiUrl}/packages/${packageId.value}`);
        done = true;
        leave();
      } catch (failure) {
        message.value = "Suppression impossible";
        messageIsError.value = true;
      } finally {
        saving.value = false;
      }
    };

    // Close the purchase picker on a click elsewhere
    const closePicker = (event) => {
      if (!event.target.closest?.(".add-lot")) pickerOpen.value = false;
    };
    onMounted(() => {
      load();
      document.addEventListener("click", closePicker);
    });
    onBeforeUnmount(() => {
      document.removeEventListener("click", closePicker);
      clearTimeout(timer);
    });

    return {
      PACKAGE_SENDERS,
      isIntermediary,
      trackingIsLink,
      cameFromApp,
      leave,
      addLot,
      addSameRef,
      candidates,
      computedOf,
      form,
      isNew,
      listingPrice,
      loading,
      message,
      messageIsError,
      money,
      pickerOpen,
      preview,
      purchaseOf,
      rateIsToday,
      rateOpen,
      rateOrigin,
      rateText,
      remove,
      removeLot,
      sameRefPurchases,
      save,
      saving,
      search,
      setup,
      sharesVisible,
      showShares,
      sourceName,
      yenToEur,
    };
  },
};
</script>

<style scoped>
.package-page {
  --text: #202b3c;
  --muted: #6f7b8d;
  --line: #e3e8ef;
  --green: #168a64;
  min-height: 100%;
  padding: var(--page-gutter);
  background: #f5f7fa;
  color: var(--text);
}
.page-heading,
.panel {
  max-width: var(--content-width);
  margin-inline: auto;
}
.page-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
}
.page-heading p {
  margin: 0 0 8px;
  color: var(--green);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.page-heading h1 {
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: var(--page-title-size);
  letter-spacing: -0.035em;
}
.page-heading span {
  display: block;
  margin-top: 8px;
  color: var(--muted);
  font-size: 14px;
}
.back {
  color: #2e6098;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
}
.panel {
  margin-bottom: 16px;
  padding: 24px 28px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
}
.empty {
  color: var(--muted);
  text-align: center;
}
.section-heading {
  display: flex;
  gap: 16px;
  margin-bottom: 18px;
}
.section-heading > b {
  display: grid;
  width: 34px;
  height: 26px;
  flex: 0 0 34px;
  place-items: center;
  border-radius: 6px;
  background: #e8f6f1;
  color: var(--green);
  font-size: 11px;
}
.section-heading h2 {
  margin: 1px 0 5px;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: var(--section-title-size);
}
.section-heading p {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
}
.grid--package {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}
.grid--shipment {
  margin-top: 14px;
}
.tracking {
  display: flex;
  align-items: center;
  gap: 8px;
}
.tracking .input {
  min-width: 0;
  flex: 1;
}
.tracking__open {
  flex: none;
  color: var(--green, #168a64);
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}
.options {
  margin-top: 14px;
}
.options__line {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
}
.field--rate {
  max-width: 260px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
}
.field small {
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
}
.input {
  width: 100%;
  min-width: 0;
  height: 38px;
  padding: 0 10px;
  border: 1px solid #cfd6df;
  border-radius: 8px;
  background: #fff;
  color: var(--text);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
}
.input:focus {
  border-color: #4aa487;
  box-shadow: 0 0 0 3px rgba(22, 138, 100, 0.1);
  outline: none;
}
.affix {
  display: flex;
  white-space: nowrap;
  align-items: center;
  gap: 6px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}
.link {
  padding: 0;
  border: 0;
  background: none;
  color: #2e6098;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}
.hint {
  margin: 10px 0 0;
  color: var(--muted);
  font-size: 13px;
}
.add-lot {
  position: relative;
  max-width: 560px;
  margin-bottom: 14px;
}
.picker {
  position: absolute;
  z-index: 20;
  top: 42px;
  right: 0;
  left: 0;
  max-height: 320px;
  margin: 0;
  padding: 6px;
  overflow: auto;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(16, 24, 39, 0.16);
  list-style: none;
}
.picker li {
  display: block;
  margin: 0;
}
.picker button {
  display: block;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 6px;
  background: none;
  text-align: left;
  cursor: pointer;
}
.picker button:hover {
  background: #f1f4f7;
}
.picker strong,
.picker small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.picker strong {
  font-size: 13px;
}
.picker small {
  color: var(--muted);
  font-size: 11px;
}
/* Columns share the width of the panel; below 880 px of panel (whatever the
   screen and sidebar), the lots switch to cards */
.lots {
  container: lots / inline-size;
}
.lots__head,
.lot {
  display: grid;
  grid-template-columns:
    minmax(130px, 2fr) minmax(84px, 1fr) minmax(112px, 1.1fr)
    repeat(2, minmax(78px, 0.9fr)) repeat(3, minmax(66px, 0.8fr)) 30px;
  align-items: center;
  gap: 10px;
}
.lots--shares .lots__head,
.lots--shares .lot {
  grid-template-columns:
    minmax(130px, 2fr) minmax(84px, 1fr) minmax(112px, 1.1fr)
    repeat(2, minmax(78px, 0.9fr)) minmax(52px, 0.5fr)
    repeat(3, minmax(66px, 0.8fr)) 30px;
}
.lots__head .num {
  text-align: right;
}
.lots__head {
  padding: 0 0 8px;
  border-bottom: 1px solid var(--line);
  color: #7d8796;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.lot {
  padding: 10px 0;
  border-bottom: 1px solid #edf0f4;
}
.lot__title {
  min-width: 0;
}
.lot__title strong,
.lot__title small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lot__link {
  display: block;
  min-width: 0;
  color: inherit;
  text-decoration: none;
}
.lot__link:hover strong {
  color: #2e6098;
  text-decoration: underline;
}
.lot__title strong {
  font-size: 13px;
}
.lot__title small {
  color: var(--muted);
  font-size: 11px;
}
.lot__label {
  display: none;
}
.input--share {
  text-align: center;
}
.computed {
  font-size: 13px;
  text-align: right;
}
.computed--total {
  font-weight: 800;
}
.remove {
  width: 30px;
  height: 30px;
  border: 1px solid #f0c9c7;
  border-radius: 7px;
  background: #fff;
  color: #c84642;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}
.modes {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 12px;
}
.mode {
  display: flex;
  max-width: 360px;
  flex: 1 1 280px;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  cursor: pointer;
}
.mode.active {
  border-color: var(--green);
  background: #f2faf6;
}
.mode strong,
.mode small {
  display: block;
}
.mode strong {
  font-size: 13px;
}
.mode small {
  margin-top: 2px;
  color: var(--muted);
  font-size: 12px;
}
.paid {
  width: 200px;
}
.summary {
  position: sticky;
  bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  box-shadow: 0 10px 30px rgba(16, 24, 39, 0.08);
}
.summary__totals {
  display: flex;
  flex-wrap: wrap;
  gap: 22px;
  margin-right: auto;
  color: var(--muted);
  font-size: 12px;
}
.summary__totals strong {
  display: block;
  color: var(--text);
  font-size: 15px;
}
.summary__total strong {
  color: var(--green);
  font-size: 18px;
}
.summary__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.message {
  width: 100%;
  margin: 0;
  color: #1f7a5a;
  font-size: 13px;
}
.message.error {
  color: #c84642;
}
.button {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
}
.button--primary {
  border-color: var(--green);
  background: var(--green);
  color: #fff;
}
.button--secondary {
  border-color: #cfd6df;
  background: #fff;
  color: #4e596a;
}
.button--danger {
  border-color: #f0c9c7;
  background: #fff;
  color: #c84642;
}
.button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
@media (max-width: 1100px) {
  .grid--package {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  /* The tracking link is long: the whole width */
  .field--tracking {
    grid-column: 1 / -1;
  }
}
/* Lots as cards */
@container lots (max-width: 880px) {
  .lots__head {
    display: none;
  }
  .lot,
  .lots--shares .lot {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: 14px 0;
  }
  .lot__title {
    grid-column: 1 / 3;
  }
  .remove {
    justify-self: end;
    grid-column: 3;
    grid-row: 1;
  }
  .lot__label {
    display: block;
    margin-bottom: 4px;
    color: var(--muted);
    font-size: 11px;
    font-weight: 700;
  }
  .computed {
    text-align: left;
  }
}
@container lots (max-width: 520px) {
  .lot,
  .lots--shares .lot {
    grid-template-columns: 1fr 1fr;
  }
  .lot__title {
    grid-column: 1 / 2;
  }
  .remove {
    grid-column: 2;
  }
}
@media (max-width: 640px) {
  .package-page {
    padding: 20px 14px;
  }
  .page-heading {
    align-items: flex-start;
    flex-direction: column-reverse;
  }
  .panel {
    padding: 18px 16px;
  }
  .grid--package {
    grid-template-columns: 1fr 1fr;
  }
  .summary {
    bottom: 0;
  }
  .summary__actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
